import type { ScheduleDto, EndPolicyDto, ScheduleResponse } from '@kidshome/api-contract';
import { tDynamic } from '@/shared/lib/i18n';
import { parseLocalDate, localDateISO, addDaysISO } from '@/shared/lib/dates';

/**
 * Client-side vocabulary + summary for the unified flexible {@link ScheduleDto} (ADR-0035), shared by
 * the chore + habit editors, their `SchedulePicker`, and the admin list pages. Replaces the removed
 * per-context `repeatType`/`weekDays` helpers — there is now ONE schedule model.
 *
 * A schedule is a `recurrence` (one_time · daily · weekly_days · monthly_day · times_per_period) plus
 * an `anchor`, a `timeOfDay` window, and an optional exact `dueTime`. Chores additionally carry an
 * {@link EndPolicyDto} (never · on a date · after N occurrences); habits never end. The API re-validates
 * every value — these lists/helpers are a UX convenience only.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Translate = (key: any, options?: any) => unknown;

/** ISO-8601 weekdays in display order (1=Mon … 7=Sun). */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** Stable short-name i18n keys for the ISO weekday numbers (`schedule.weekday.*`). */
export const WEEKDAY_KEYS: Record<number, string> = {
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
  7: 'sun',
};

/** The time-of-day window options (segmented control). */
export const TIME_OF_DAY = ['any', 'morning', 'afternoon', 'evening'] as const;

/** Period options for the flexible `times_per_period` kind (habit only). */
export const PERIODS = ['week', 'month'] as const;

/** The default schedule a fresh chore/habit starts from: every day, any time, scheduled anchor. */
export function defaultSchedule(): ScheduleDto {
  return {
    recurrence: { kind: 'daily', interval: 1 },
    anchor: 'scheduled',
    timeOfDay: 'any',
    dueTime: null,
    reminderTime: null,
  };
}

/** The default end policy a fresh chore starts from: never ends. */
export function defaultEndPolicy(): EndPolicyDto {
  return { kind: 'never' };
}

/**
 * Build a {@link ScheduleDto} from a {@link ScheduleResponse} for editor seeding (the shapes match;
 * this normalizes optional fields + applies the interval default so the picker never sees a hole).
 */
export function scheduleFromResponse(s: ScheduleResponse): ScheduleDto {
  return {
    recurrence: {
      kind: s.recurrence.kind,
      interval: s.recurrence.interval ?? 1,
      ...(s.recurrence.weekDays ? { weekDays: [...s.recurrence.weekDays] } : {}),
      ...(s.recurrence.monthDays ? { monthDays: [...s.recurrence.monthDays] } : {}),
      ...(s.recurrence.count !== undefined ? { count: s.recurrence.count } : {}),
      ...(s.recurrence.period ? { period: s.recurrence.period } : {}),
    },
    anchor: s.anchor,
    timeOfDay: s.timeOfDay,
    dueTime: s.dueTime,
    reminderTime: s.reminderTime,
  };
}

/** Seed a chore's {@link EndPolicyDto} from the template's `endDate`/`endAfterCount` mirror fields. */
export function endPolicyFromTemplate(
  endDate: string | null,
  endAfterCount: number | null,
): EndPolicyDto {
  if (endAfterCount !== null) return { kind: 'after', count: endAfterCount };
  if (endDate !== null) return { kind: 'on', date: endDate };
  return { kind: 'never' };
}

/**
 * Seed a habit's {@link EndPolicyDto} from its `endDate` (ADR-0056). A habit has no `after N` end
 * (it has no materialized occurrences to count), so the policy is only ever `never` or `on a date`.
 */
export function endPolicyFromHabit(endDate: string | null | undefined): EndPolicyDto {
  return endDate ? { kind: 'on', date: endDate } : { kind: 'never' };
}

/**
 * The `endDate` (or `null`) a habit's {@link EndPolicyDto} maps to for the create/update payload. A
 * habit only carries `never`/`on`, so anything that isn't `on` with a date is open-ended (null).
 */
export function habitEndDate(endPolicy: EndPolicyDto): string | null {
  return endPolicy.kind === 'on' && endPolicy.date ? endPolicy.date : null;
}

/** The preset chip ids, in display order. `timesPerWeek` is habit-only (flexible kind). */
export type PresetId =
  | 'everyDay'
  | 'weekdays'
  | 'weekends'
  | 'monWedFri'
  | 'everyTwoWeeks'
  | 'monthly'
  | 'timesPerWeek'
  | 'justOnce'
  | 'custom';

/** A preset chip: its id (i18n label key + glyph) and the concrete recurrence it writes. */
interface Preset {
  readonly id: PresetId;
  readonly glyph: string;
  /** The recurrence this preset maps to; `null` for `custom` (which only opens the disclosure). */
  readonly recurrence: ScheduleDto['recurrence'] | null;
  /** Restrict to habit context (the flexible `times_per_period` kind). */
  readonly habitOnly?: boolean;
}

/** The ordered preset chips. Selecting one overwrites `value.recurrence` wholesale. */
export const PRESETS: readonly Preset[] = [
  { id: 'everyDay', glyph: '📅', recurrence: { kind: 'daily', interval: 1 } },
  {
    id: 'weekdays',
    glyph: '🎒',
    recurrence: { kind: 'weekly_days', interval: 1, weekDays: [1, 2, 3, 4, 5] },
  },
  {
    id: 'weekends',
    glyph: '🌅',
    recurrence: { kind: 'weekly_days', interval: 1, weekDays: [6, 7] },
  },
  {
    id: 'monWedFri',
    glyph: '✨',
    recurrence: { kind: 'weekly_days', interval: 1, weekDays: [1, 3, 5] },
  },
  {
    id: 'everyTwoWeeks',
    glyph: '🔁',
    recurrence: { kind: 'weekly_days', interval: 2, weekDays: [1] },
  },
  { id: 'monthly', glyph: '🗓️', recurrence: { kind: 'monthly_day', interval: 1, monthDays: [1] } },
  {
    id: 'timesPerWeek',
    glyph: '🎯',
    recurrence: { kind: 'times_per_period', count: 3, period: 'week' },
    habitOnly: true,
  },
  { id: 'justOnce', glyph: '📌', recurrence: { kind: 'one_time', interval: 1 } },
  { id: 'custom', glyph: '⚙️', recurrence: null },
] as const;

/** Deep-equal for the two arrays a recurrence may carry (order-insensitive). */
function sameSet(a: readonly number[] | undefined, b: readonly number[] | undefined): boolean {
  const sa = [...(a ?? [])].sort((x, y) => x - y);
  const sb = [...(b ?? [])].sort((x, y) => x - y);
  return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
}

/**
 * Which preset chip (if any) the given recurrence exactly matches — so re-opening an editor lights the
 * right chip instead of always falling to "Custom". Returns `"custom"` when nothing matches a chip.
 */
export function matchPreset(rec: ScheduleDto['recurrence']): PresetId {
  const interval = rec.interval ?? 1;
  for (const p of PRESETS) {
    if (!p.recurrence) continue;
    const r = p.recurrence;
    if (r.kind !== rec.kind) continue;
    if (r.kind === 'daily' || r.kind === 'one_time') {
      if ((r.interval ?? 1) === interval) return p.id;
    } else if (r.kind === 'weekly_days') {
      if ((r.interval ?? 1) === interval && sameSet(r.weekDays, rec.weekDays)) return p.id;
    } else if (r.kind === 'monthly_day') {
      if ((r.interval ?? 1) === interval && sameSet(r.monthDays, rec.monthDays)) return p.id;
    } else if (r.kind === 'times_per_period') {
      if (r.count === rec.count && r.period === rec.period) return p.id;
    }
  }
  return 'custom';
}

/** Localized, comma-joined weekday list in ISO order, e.g. "Mon, Wed, Fri". */
function weekdayList(days: readonly number[] | undefined, t: Translate): string {
  return [...(days ?? [])]
    .sort((a, b) => a - b)
    .map((d) => tDynamic(t, `schedule.weekday.${WEEKDAY_KEYS[d]}`))
    .join(', ');
}

/** Localized, comma-joined day-of-month list (1-based), e.g. "1, 15". */
function monthDayList(days: readonly number[] | undefined): string {
  return [...(days ?? [])].sort((a, b) => a - b).join(', ');
}

/**
 * The recurrence half of the summary — a single translated phrase built from a semantic key + params
 * (NEVER string-glued grammar, ADR-0025). Each branch picks the key whose translation already encodes
 * the grammar for that pattern.
 */
function recurrencePhrase(rec: ScheduleDto['recurrence'], t: Translate): string {
  const interval = rec.interval ?? 1;
  switch (rec.kind) {
    case 'one_time':
      return tDynamic(t, 'schedule.summary.oneTime');
    case 'daily':
      return interval === 1
        ? tDynamic(t, 'schedule.summary.daily')
        : tDynamic(t, 'schedule.summary.everyNDays', { count: interval });
    case 'weekly_days': {
      const days = weekdayList(rec.weekDays, t);
      return interval === 1
        ? tDynamic(t, 'schedule.summary.weekly', { days })
        : tDynamic(t, 'schedule.summary.weeklyEveryN', { count: interval, days });
    }
    case 'monthly_day': {
      const days = monthDayList(rec.monthDays);
      return interval === 1
        ? tDynamic(t, 'schedule.summary.monthly', { days })
        : tDynamic(t, 'schedule.summary.monthlyEveryN', { count: interval, days });
    }
    case 'times_per_period':
      return tDynamic(t, `schedule.summary.timesPer.${rec.period ?? 'week'}`, {
        count: rec.count ?? 1,
      });
    default:
      return '';
  }
}

/** The end-policy clause of a chore summary (or empty for never / a habit). */
function endPhrase(endPolicy: EndPolicyDto | undefined, t: Translate, locale: string): string {
  if (!endPolicy || endPolicy.kind === 'never') return '';
  if (endPolicy.kind === 'after') {
    return tDynamic(t, 'schedule.summary.endsAfter', { count: endPolicy.count ?? 1 });
  }
  // kind === "on"
  if (!endPolicy.date) return '';
  const date = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  }).format(parseLocalDate(endPolicy.date));
  return tDynamic(t, 'schedule.summary.endsOn', { date });
}

/**
 * The persistent live summary line for a schedule, e.g. "Every 2 weeks on Mon, Wed · mornings · until
 * 13 Sep". Built by joining independent translated *clauses* with a middot — each clause is a complete
 * grammatical unit, so this is NOT translated-grammar concatenation (ADR-0025): the dot is a list
 * separator, not glue inside a sentence. `endPolicy` is chore-only (habits pass `undefined`).
 */
export function scheduleSummary(
  schedule: ScheduleDto | ScheduleResponse,
  endPolicy: EndPolicyDto | undefined,
  t: Translate,
  locale = 'en',
): string {
  const parts: string[] = [recurrencePhrase(schedule.recurrence, t)];

  // Time clause: the dueTime (if set) takes precedence over the window label; "any" with no time is
  // implicit and omitted to keep the common case short.
  if (schedule.dueTime) {
    parts.push(tDynamic(t, 'schedule.summary.atTime', { time: schedule.dueTime }));
  } else if (schedule.timeOfDay !== 'any') {
    parts.push(tDynamic(t, `schedule.summary.timeOfDay.${schedule.timeOfDay}`));
  }

  const ends = endPhrase(endPolicy, t, locale);
  if (ends) parts.push(ends);

  return parts.filter(Boolean).join(' · ');
}

/** The ISO-8601 weekday (1=Mon … 7=Sun) of a `YYYY-MM-DD` local date. */
function isoWeekday(localISO: string): number {
  const [y, m, d] = localISO.split('-').map(Number);
  const dow = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12)).getUTCDay();
  return ((dow + 6) % 7) + 1; // 0=Sun..6=Sat → 1=Mon..7=Sun
}

/** Whole-day epoch index of a `YYYY-MM-DD` local date (UTC midnight anchored). */
function dayIndex(localISO: string): number {
  const [y, m, d] = localISO.split('-').map(Number);
  return Math.floor(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / 86_400_000);
}

/** Whole calendar days between two `YYYY-MM-DD` local dates (`b − a`). Mirrors the API's `daysBetween`. */
function daysBetweenISO(a: string, b: string): number {
  return dayIndex(b) - dayIndex(a);
}

/** Whole calendar months between two `YYYY-MM-DD` local dates (`b − a`), by year·12 + month. */
function monthsBetweenISO(a: string, b: string): number {
  const [ay, am] = a.split('-').map(Number);
  const [by, bm] = b.split('-').map(Number);
  return ((by ?? 0) - (ay ?? 0)) * 12 + ((bm ?? 0) - (am ?? 0));
}

/** The epoch day index of the Monday starting the ISO week containing a `YYYY-MM-DD` local date. */
function weekStartIndexISO(localISO: string): number {
  return dayIndex(localISO) - (isoWeekday(localISO) - 1);
}

/**
 * A best-effort next household-local date (`YYYY-MM-DD`) a chore template will next produce an
 * occurrence, or `null` if it never will again — a UX hint only (the server owns real scheduling).
 * Daily/weekly scan forward from the later of today/start within the active window, far enough to
 * guarantee finding the next hit for the recurrence's interval (daily: `interval` days; weekly:
 * `14 * interval` days) — a fixed window would wrongly report "never again" for an interval wider
 * than it. Monthly iterates the interval-eligible *months* directly (a day window can't work there:
 * with a high day-of-month like 29/30/31 and interval > 1 the next hit can be years away, e.g. every
 * 12 months on the 29th anchored to February → the next leap year). The flexible `times_per_period`
 * kind has no fixed calendar day, so it returns the earliest in-window day. `endDate` is the
 * inclusive last day for end policy `on`; `endAfterCount` is not modelled here.
 *
 * The interval (every N days/weeks/months) is checked against `startDate` exactly as the domain's
 * `Schedule.occursOn` does (`apps/api/.../schedule.vo.ts`) — `daysBetween(start, date) % interval`,
 * `weeks % interval` (weeks counted from each date's ISO week-start), `monthsBetween % interval` — so
 * this never reports a "next run" on a day the template doesn't actually produce an occurrence.
 */
export function nextRunISO(
  schedule: ScheduleDto | ScheduleResponse,
  startDate: string,
  endDate: string | null,
  timeZone: string,
): string | null {
  const today = localDateISO(timeZone);
  const from = today > startDate ? today : startDate;
  const within = (iso: string): boolean => iso >= startDate && (endDate === null || iso <= endDate);
  const rec = schedule.recurrence;
  const interval = rec.interval ?? 1;

  if (rec.kind === 'one_time') {
    return within(startDate) && startDate >= today ? startDate : null;
  }
  if (rec.kind === 'times_per_period') {
    // Flexible frequency: every in-range day is eligible (no interval, no fixed day — occursOn's own
    // `times_per_period => true` branch).
    return within(from) ? from : null;
  }
  if (rec.kind === 'daily') {
    // The day-index modulo cycles with period exactly `interval`, so scanning `interval` consecutive
    // candidates from `from` always reaches the next hit — a fixed window (unlike the weekly_days/
    // monthly_day windows below, which already scale with interval) would miss it once interval grows
    // past the window.
    for (let i = 0; i < interval; i += 1) {
      const candidate = addDaysISO(from, i);
      if (endDate !== null && candidate > endDate) return null;
      if (daysBetweenISO(startDate, candidate) % interval === 0 && within(candidate)) {
        return candidate;
      }
    }
    return null;
  }
  if (rec.kind === 'weekly_days') {
    const days = new Set(rec.weekDays ?? []);
    if (days.size === 0) return null;
    for (let i = 0; i < 14 * interval; i += 1) {
      const candidate = addDaysISO(from, i);
      if (endDate !== null && candidate > endDate) return null;
      if (!days.has(isoWeekday(candidate)) || !within(candidate)) continue;
      const weeks = Math.floor((weekStartIndexISO(candidate) - weekStartIndexISO(startDate)) / 7);
      if (weeks % interval === 0) return candidate;
    }
    return null;
  }
  // monthly_day: iterate the interval-eligible months (anchored to startDate, mirroring occursOn's
  // `monthsBetween % interval` rule) rather than scanning days — eligible months may repeatedly lack
  // a high configured day-of-month (29/30/31), pushing the next real occurrence months or years out.
  const monthDays = [...(rec.monthDays ?? [])].sort((a, b) => a - b);
  if (monthDays.length === 0) return null;
  const [fy, fm] = from.split('-').map(Number);
  let year = fy ?? 1970;
  // 1-based month, advanced to the first interval-eligible month at or after `from`.
  let month = (fm ?? 1) + ((interval - (monthsBetweenISO(startDate, from) % interval)) % interval);
  year += Math.floor((month - 1) / 12);
  month = ((month - 1) % 12) + 1;
  // Feb-29 recurs within at most 8 years (96 months), the worst legitimate gap — so this bound covers
  // every pattern that can ever fire; exhausting it means it genuinely never hits again (e.g. day 31
  // with an interval landing only on 30-day months).
  const maxEligibleMonths = Math.max(2, Math.ceil(96 / interval) + 1);
  for (let i = 0; i < maxEligibleMonths; i += 1) {
    const mm = String(month).padStart(2, '0');
    if (endDate !== null && `${year}-${mm}-01` > endDate) return null;
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    for (const day of monthDays) {
      if (day > daysInMonth) continue;
      const candidate = `${year}-${mm}-${String(day).padStart(2, '0')}`;
      if (candidate >= from && within(candidate)) return candidate;
    }
    month += interval;
    year += Math.floor((month - 1) / 12);
    month = ((month - 1) % 12) + 1;
  }
  return null;
}
